import * as React from "react";

import {
  actions,
  PlotPanelState,
  useVizDispatch,
} from "@/shared/state/visualization";
import { ViewportSyncMode } from "@/shared/state/visualization/schema";

import { useSeriesAlignment } from "../hooks/useSeriesAlignment";
import { ZoomMode } from "../messaging/commands";
import { PlotTool } from "../plotTools/PlotTool";

import { effectiveViewportSyncMode } from "./effectiveViewportSyncMode";
import { PlotPanelContext, Region } from "./PlotPanelContext";

interface PlotPanelProviderProps extends React.PropsWithChildren {
  state: PlotPanelState;
}

export function PlotPanelProvider({ children, state }: PlotPanelProviderProps) {
  const dispatch = useVizDispatch();
  const [activeTool, setActiveTool] = React.useState<PlotTool>(PlotTool.Move);

  const alignment = useSeriesAlignment(state);
  // Config presence, not `alignment !== undefined`: an event-anchored panel is
  // aligned while its events are still loading.
  const isAligned = state.config?.xAxisAlignment !== undefined;

  const viewportSyncMode = effectiveViewportSyncMode(
    state.config?.viewportSyncMode,
    isAligned,
  );

  const [isLegendShown, setIsLegendShown] = React.useState(true);
  const [selectedRegion, setSelectedRegion] = React.useState<Region | null>(
    null,
  );
  const [zoomMode, setZoomMode] = React.useState<ZoomMode>("x");

  // Writes the persisted setting; while the x axis is aligned, `viewportSyncMode`
  // above ignores it.
  const setViewportSyncMode = React.useCallback(
    (mode: ViewportSyncMode) => {
      dispatch(
        actions.setPlotPanelConfig(state.id, { viewportSyncMode: mode }),
      );
    },
    [dispatch, state.id],
  );

  React.useEffect(
    function resetSelectedRegionWhenToolChanges() {
      setSelectedRegion(null);
    },
    [activeTool, setSelectedRegion],
  );

  return (
    <PlotPanelContext.Provider
      value={{
        activeTool,
        setActiveTool,
        alignment,
        isAligned,
        viewportSyncMode,
        setViewportSyncMode,
        isLegendShown,
        setIsLegendShown,
        selectedRegion,
        setSelectedRegion,
        zoomMode,
        setZoomMode,
      }}
    >
      {children}
    </PlotPanelContext.Provider>
  );
}
